import { useSuspenseQuery } from "@tanstack/react-query";
import axios from "axios";
import { getCockpitConfigs } from "config";
import { apiAxiosOptions, apiFetchSearchMasters } from "modules/Drupal/Drupal";
import { sleep } from "modules/misc";
import { TCard } from "./CardModel";
import { useTotalMasters } from "./CardMasterModel";
import { useRebuildSearchValues } from "./CardDataModel";
import { TRoute } from "./RouteModel";

const { baseUrl } = getCockpitConfigs();

export type TSearchMaster = {
  id: any;
  label: string;
  api: string;
  searchType: string;
  provideKey: string;
  useKey: string;
  options: any[] | undefined;
};

export const useSearchMaster = () => {
  const { getTotalMaster } = useTotalMasters();
  const { data } = useFetchSearchMasters();

  const getSearchMastersAll = () => data as TSearchMaster[];

  const getSearchMaster = (id: TSearchMaster["id"]): TSearchMaster => {
    return data.find((master: TSearchMaster) => master.id === id);
  };

  const getSearchMasterByProvideKey = (key: TSearchMaster["provideKey"]): TSearchMaster => {
    return data.find((master: TSearchMaster) => master.provideKey === key);
  };

  const getSearchMasters = (ids: TSearchMaster["id"][]): TSearchMaster[] => {
    const masters: TSearchMaster[] = ids.map((id) => getSearchMaster(id));
    return masters;
  };

  const getSearchMastersByCard = (total_id: TCard["total_id"]): TSearchMaster[] => {
    if (!total_id) return [];
    const totalMaster = getTotalMaster(total_id);
    return getSearchMasters(totalMaster.searchList);
  };

  return {
    getSearchMaster,
    getSearchMasterByProvideKey,
    getSearchMastersAll,
    getSearchMasters,
    getSearchMastersByCard,
  };
};

export const useFetchSearchMasters = () => {
  return useSuspenseQuery({
    queryKey: ["useFetchSearchMasters"],
    queryFn: apiFetchSearchMasters,
  });
};

export const useSearchOptions = (searchMaster: TSearchMaster, searchValues?: any) => {
  const { data } = useSuspenseQuery({
    queryKey: ["fetchedSearchOptions", { searchMaster, searchValues }],
    queryFn: () => fetchData(searchMaster, searchValues),
  });
  return { data };
};

export const useCardSearchOptions = (target: TCard | TRoute, searchMaster: TSearchMaster, searchValuesOrg: any) => {
  const searchValues = useRebuildSearchValues(target, searchValuesOrg);
  const { data, refetch } = useSuspenseQuery({
    queryKey: ["fetchedSearchOptions", { searchMaster, searchValues }],
    queryFn: () => fetchData(searchMaster, searchValues),
  });

  return { data, refetch };
};

export const usePluralCardSearchOptions = (card: TCard, searchMasters: TSearchMaster[], searchValuesOrg: any) => {
  const searchValues = useRebuildSearchValues(card, searchValuesOrg);
  const { data } = useSuspenseQuery({
    queryKey: ["fetchedSearchPluralOptions", { searchMasters, searchValues }],
    queryFn: () => fetchPluralData(searchMasters, searchValues),
  });
  return { data };
};

const fetchData = async (searchMaster: TSearchMaster, searchValues: any) => {
  // https://cips.cloud.redmine.jp/issues/17112
  // SearchMasterが "TOSHI_CD" の場合は、"TODOFUKEN_CD" での検索必須。
  // [ToDo] Drupal側のSearchMasterで、他のフィルタ必須設定を可能とし、動的にする。
  if (searchMaster.provideKey === "TOSHI_CD" && !searchValues["TODOFUKEN_CD"]) return [];
  // https://cips.cloud.redmine.jp/issues/17590
  // SearchMasterが "JIGYOSHO_CD" の場合は、"SHITEN_CD" での検索必須。
  // [ToDo] Drupal側のSearchMasterで、他のフィルタ必須設定を可能とし、動的にする。
  if (searchMaster.provideKey === "JIGYOSHO_CD" && !searchValues["SHITEN_CD"]) return [];

  const apiOptions = apiAxiosOptions();
  const result = await axios
    .get(baseUrl + searchMaster.api, {
      ...apiOptions,
      params: {
        master_type: "search_master",
        master_id: searchMaster.id,
        search_values: searchValues,
      },
    })
    .then(await sleep(0));
  return result.data ? result.data["records"] : [];
};

const fetchPluralData = async (searchMasters: TSearchMaster[], searchValues: any) => {
  if (!searchValues) return [];
  const apiOptions = apiAxiosOptions();
  const responses = searchMasters.map((searchMaster) => {
    return axios.get(baseUrl + searchMaster.api, {
      ...apiOptions,
      params: {
        master_type: "search_master",
        master_id: searchMaster.id,
        search_values: searchValues,
      },
    });
  });
  return Promise.all(responses).then((values) => {
    return values.map((value) => {
      return {
        master_id: value.config.params.master_id,
        records: value.data ? value.data["records"] : [],
      };
    });
  });
};
